/**
 * useWaConversations — gestiona la carga, estado y preview de conversaciones WhatsApp.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ESTADOS_VISIBLES, vaEnLaBandeja } from '@/lib/whatsapp/estadosDeConversacion';
import { supabase } from '@/lib/supabase';
import { traceSpan } from '@/lib/trace';
import { devLog } from '@/lib/utils';
import { logError } from '@/lib/logger';

export interface WaConversation {
  id: string;
  display_id: number;
  /**
   * 🪤 PUEDE FALTAR, Y ANTES NO PODÍA. WhatsApp identifica a mucha gente con un
   * LID en vez de su número, y desde ADR 0047 ese identificador NO se guarda
   * como si fuera un teléfono: la columna queda nula.
   *
   * Declararlo `string` a secas hacía que el compilador dejara pasar
   * `contact_phone.toLowerCase()`, y eso revienta la pantalla entera en cuanto
   * un contacto no tiene número.
   */
  contact_phone: string | null;
  contact_jid?: string | null;
  /** El @usuario de WhatsApp (Meta), cuando el contacto no muestra su número. */
  contact_username?: string | null;
  contact_name: string | null;
  contact_photo_url?: string | null;
  status: string;
  assigned_agent_id: string | null;
  ai_handling: boolean;
  last_message_at: string | null;
  last_message_body?: string | null;
  last_message_type?: string | null;
  last_message_direction?: string | null;
  last_message_status?: string | null;
  unread_count: number;
  message_count: number;
  tags: string[];
  crm_lead_id: string | null;
  /** Contacto canonico (`crm_contacts.id`). Null = este chat todavia no tiene ficha de cliente. */
  contact_id?: string | null;
  sla_first_response_breached: boolean;
  sla_resolution_breached: boolean;
  sla_first_response_due_at: string | null;
  sla_resolution_due_at: string | null;
  is_pinned?: boolean;
  muted_until?: string | null;
  snoozed_until?: string | null;
  /** Candado puesto desde el celular. Se muestra con 🔒; no se esconde: la línea es del negocio. */
  con_candado?: boolean;
  typing?: boolean;
  phone_line_id?: string | null;
  /** La entrada por la que llegó. Canónico desde el ADR 0022. */
  inbox_id?: string | null;
  /** Cómo se identifica en su canal: teléfono, correo, id de visitante… */
  contact_identifier?: string | null;
  /** `phone` · `email` · `web` · `external`. */
  contact_kind?: string | null;
  created_at?: string | null;
  /** "Última conexión" derivada vía presence.update (Fase 1, aproximada). */
  contact_last_seen_at?: string | null;
}

interface Options {
  tenantId: string | null;
  statusFilter: string;
  isAgent: boolean;
  userId: string | undefined;
  phoneLineId?: string | null;
  /**
   * Entrada (inbox) por la que filtrar, para canales que NO son WhatsApp.
   *
   * 🪤 ES OTRO CAMPO, NO EL MISMO. Una conversación del chat del sitio no tiene
   * `phone_line_id`: filtrar por ahí devolvería cero y parecería que el canal
   * está vacío en vez de mal filtrado.
   */
  inboxId?: string | null;
}

/** Browser-cache las fotos de perfil para que el primer render de la
 *  vista las pinte instantes (sin parpadeo por descarga). El navegador
 *  cachea por URL — un `new Image().src = url` dispara la descarga en
 *  background; al renderizar `<img src=url>` después, la imagen sale
 *  del cache. */
function preloadAvatars(convs: WaConversation[]): void {
  for (const c of convs) {
    if (c.contact_photo_url) {
      const img = new Image();
      img.src = c.contact_photo_url;
    }
  }
}

/** Equality fast-path: dos listas son "iguales" si tienen los mismos ids
 *  en el mismo orden con los campos que afectan al render (timestamps,
 *  contadores, status, pin). Skipea re-renders innecesarios cuando el
 *  refresh de fondo trae data sin cambios. */
function areConvListsEqual(a: WaConversation[], b: WaConversation[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ap = a[i];
    const bp = b[i];
    if (ap.id !== bp.id) return false;
    if (ap.last_message_at !== bp.last_message_at) return false;
    if (ap.unread_count !== bp.unread_count) return false;
    if (ap.status !== bp.status) return false;
    if (ap.is_pinned !== bp.is_pinned) return false;
    if (ap.last_message_body !== bp.last_message_body) return false;
    if (ap.last_message_status !== bp.last_message_status) return false;
  }
  return true;
}

/** Tamaño de página. La 1ra carga trae PAGE_SIZE; "cargar más" pide otra tanda por cursor. */
const PAGE_SIZE = 500;

/** Columnas de la lista. En scope de módulo para que fetchForFilter y loadMore usen las MISMAS. */
const WA_CONV_COLUMNS = [
  'id', 'display_id', 'contact_phone', 'contact_jid', 'contact_username', 'contact_name', 'contact_photo_url',
  'status', 'assigned_agent_id', 'ai_handling',
  'last_message_at', 'last_message_body', 'last_message_type',
  'last_message_direction', 'last_message_status',
  'unread_count', 'message_count', 'tags', 'crm_lead_id',
  // El puente al cliente: `contact_id` apunta al contacto canonico (`crm_contacts`), que es la
  // llave de Cliente 360. Sin esta columna la columna derecha no puede ofrecer «ver ficha».
  'contact_id',
  'sla_first_response_breached', 'sla_resolution_breached',
  'sla_first_response_due_at', 'sla_resolution_due_at',
  'is_pinned', 'muted_until', 'snoozed_until', 'phone_line_id', 'created_at',
  'con_candado', // el dueño le puso candado al chat en el celular (CHATS_LOCK del fork)
  'contact_last_seen_at',
  // Forma canónica del canal y de la identidad (ADR 0022). Van SIEMPRE, aunque
  // hoy casi todas las conversaciones sean de WhatsApp: sin ellas, la primera
  // conversación de otro canal aparece sin nombre y sin origen.
  'inbox_id', 'contact_identifier', 'contact_kind',
].join(', ');


/** Aplica el filtro de estado de la vista normal a una query. Única fuente para 1ra carga y loadMore. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyStatusFilter(q: any, forFilter: string): any {
  if (forFilter === 'active') return q.in('status', ['pending', 'open']);
  if (forFilter === 'pending') return q.eq('status', 'pending');
  if (forFilter === 'snoozed') return q.eq('status', 'snoozed');
  if (forFilter === 'resolved') return q.eq('status', 'resolved');
  if (forFilter === 'archived') return q.eq('status', 'archived');
  return q.in('status', ESTADOS_VISIBLES);
}

/** ¿Esta conversación pertenece a la vista principal (no al overlay de archivados)? */
function matchesMainFilter(status: string, forFilter: string): boolean {
  if (forFilter === 'active') return status === 'pending' || status === 'open';
  if (forFilter === 'archived') return status === 'archived';
  if (forFilter === 'pending' || forFilter === 'snoozed' || forFilter === 'resolved') return status === forFilter;
  return vaEnLaBandeja(status);
}

export function useWaConversations({ tenantId, statusFilter, isAgent, userId, phoneLineId, inboxId }: Options) {
  // SINGLE SOURCE OF TRUTH — `conversations` contiene la vista actual
  // (statusFilter) UNIDA con todos los archivados. Archived es solo una
  // etiqueta (`status === 'archived'`), no una entidad separada. La página
  // deriva la lista normal filtrando `status !== 'archived'` y el overlay
  // con el opuesto. Esto evita el bug de dos listas desincronizadas que
  // teníamos cuando archived vivía en su propio state — las mutations
  // (mark-as-read, pin, archive, mute, etc.) operan sobre UN solo array y
  // ambas vistas (lista + overlay) se actualizan automáticamente.
  /**
   * Último error de carga, para que la PÁGINA lo muestre.
   * ⚠️ Este hook NO puede usar useAdminToast: WaInboxNavProvider lo consume por
   * encima del <Frame> donde vive AdminToastProvider (rompía toda la app).
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [conversations, setConversationsRaw] = useState<WaConversation[]>([]);
  const [loading, setLoading] = useState(true);
  // Paginación por cursor: hasMore = la última página vino LLENA (puede haber más).
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadRef = useRef<(() => Promise<void>) | null>(null);
  // Cache stale-while-revalidate por vista (tenant + filter + canal + agente).
  // Cuando el usuario cambia a una vista ya visitada, mostramos la lista
  // cacheada AL TOQUE y refrescamos en background — sin skeleton, sin lag.
  // Solo la primera visita de una vista paga el roundtrip; las siguientes
  // son instantáneas. Mismo patrón Slack/Linear.
  const cacheRef = useRef<Map<string, WaConversation[]>>(new Map());
  // viewKey actual: sirve para que `setConversations` (wrapped) sincronice
  // el cache cuando el page hace mutations optimistas (archive, pin, etc.).
  const currentViewKeyRef = useRef<string>('');

  // Wrapper de setConversations: las mutations externas (pin, mute, archive,
  // tags, etc.) también actualizan el cache de la vista actual. Sin esto,
  // volver a una vista cacheada mostraba data divergente por 1-2s.
  const setConversations = useCallback<React.Dispatch<React.SetStateAction<WaConversation[]>>>((updater) => {
    setConversationsRaw((prev) => {
      const next = typeof updater === 'function'
        ? (updater as (p: WaConversation[]) => WaConversation[])(prev)
        : updater;
      if (currentViewKeyRef.current) {
        cacheRef.current.set(currentViewKeyRef.current, next);
      }
      return next;
    });
  }, []);

  // Función núcleo: corre la query para el filter pedido y guarda en cache.
  // Cache hit → render instant; miss → skeleton breve. Stale-while-revalidate.
  const fetchForFilter = useCallback(async (forFilter: string): Promise<void> => {
    if (!tenantId) return;
    const viewKey = `${tenantId}|${forFilter}|${phoneLineId ?? ''}|${inboxId ?? ''}|${isAgent ? userId ?? '' : '*'}`;
    currentViewKeyRef.current = viewKey;
    const cached = cacheRef.current.get(viewKey);
    if (cached) {
      setConversationsRaw(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const buildBase = () => {
      let q = supabase
        .from('wa_conversations')
        .select(WA_CONV_COLUMNS)
        .eq('tenant_id', tenantId)
        .order('last_message_at', { ascending: false, nullsFirst: false })
        // 1ra carga: PAGE_SIZE filas (< 1000, el techo de PostgREST). Los CONTADORES
        // del sidebar NO dependen de este cap (salen de fn_wa_inbox_counts sobre todas
        // las filas); para pasar de acá está loadMore() por cursor. react-window
        // virtualiza la lista, así que el costo de render es constante.
        .limit(PAGE_SIZE);
      if (isAgent && userId) {
        q = q.or(`assigned_agent_id.eq.${userId},assigned_agent_id.is.null`);
      }
      if (inboxId) {
        q = q.eq('inbox_id', inboxId);
      }
      if (phoneLineId) {
        q = q.eq('phone_line_id', phoneLineId);
      }
      return q;
    };

    // Query principal según el statusFilter de la vista normal.
    const mainQuery = applyStatusFilter(buildBase(), forFilter);

    // Cuando la vista normal NO es 'archived', traemos en paralelo los
    // archived para que vivan en el mismo array. Esto es lo que permite
    // que `archivedConversations` y `nonArchivedConversations` sean derived
    // del mismo state — sin doble fuente, sin sincronización manual.
    const archivedQuery = forFilter === 'archived'
      ? null
      : buildBase().eq('status', 'archived');

    const [mainRes, archivedRes] = await Promise.all([
      mainQuery,
      archivedQuery ?? Promise.resolve({ data: [] as Record<string, unknown>[] }),
    ]);

    // 🪤 supabase-js NO lanza: devuelve { error }. Antes no se leía, así que ante
    // un fallo (RLS, timeout, red) `data` venía null, se pisaba la cache con []
    // y el agente veía "no hay conversaciones" — indistinguible de una bandeja
    // vacía real. Ahora conservamos la última lista buena y avisamos una vez.
    const mainErr = (mainRes as { error?: { message?: string } | null }).error;
    if (mainErr) {
      logError('wa_conversations_load_failed', new Error(mainErr.message ?? 'unknown'));
      setLoadError(mainErr.message ?? 'No se pudieron cargar las conversaciones');
      setLoading(false);
      return;
    }
    setLoadError(null);

    // ¿La página principal vino llena? Entonces puede haber más → habilitamos loadMore.
    if (currentViewKeyRef.current === viewKey) {
      setHasMore((mainRes.data?.length ?? 0) >= PAGE_SIZE);
    }

    let data = [
      ...(mainRes.data ?? []),
      ...(archivedRes.data ?? []),
    ];

    // 🔧 Deduplicar por ID — en caso de que el mismo chat aparezca dos veces
    // (bug raro pero reportado) — esto lo elimina silenciosamente.
    const seen = new Set<string>();
    data = (data ?? []).filter((c) => {
      const id = c.id as string;
      if (seen.has(id)) {
        devLog.warn('Duplicate conversation filtered out:', id);
        return false;
      }
      seen.add(id);
      return true;
    });

    const convs = ((data ?? []) as Record<string, unknown>[]).map((c) => ({
      ...c,
      tags: (c.tags as string[]) ?? [],
      contact_photo_url: (c.contact_photo_url as string) ?? null,
      last_message_body: (c.last_message_body as string) ?? null,
      last_message_type: (c.last_message_type as string) ?? null,
      last_message_direction: (c.last_message_direction as string) ?? null,
      last_message_status: (c.last_message_status as string) ?? null,
      sla_first_response_breached: (c.sla_first_response_breached as boolean) ?? false,
      sla_resolution_breached: (c.sla_resolution_breached as boolean) ?? false,
      sla_first_response_due_at: (c.sla_first_response_due_at as string) ?? null,
      sla_resolution_due_at: (c.sla_resolution_due_at as string) ?? null,
      is_pinned: (c.is_pinned as boolean) ?? false,
      muted_until: (c.muted_until as string) ?? null,
      snoozed_until: (c.snoozed_until as string) ?? null,
      typing: false,
    })) as WaConversation[];

    // Cargar preview del último mensaje para conversaciones sin datos
    const needsPreview = convs.filter((c) => (!c.last_message_body || !c.last_message_direction) && c.message_count > 0);
    if (needsPreview.length > 0) {
      const ids = needsPreview.map((c) => c.id);
      // RPC DISTINCT ON (no un select con .in sin límite): traer TODOS los mensajes
      // de N conversaciones para quedarnos con el último de cada una truncaba en
      // 1000 y dejaba conversaciones sin preview en silencio. El RPC devuelve una
      // fila por conversación.
      const { data: lastMsgs } = await supabase.rpc('fn_wa_last_message_previews', { p_ids: ids });

      if (lastMsgs) {
        const previewMap = new Map<string, { body: string; type: string; direction: string; status: string }>();
        for (const m of lastMsgs as Record<string, unknown>[]) {
          const cid = m.conversation_id as string;
          if (!previewMap.has(cid)) {
            previewMap.set(cid, {
              body: (m.body as string) ?? '',
              type: (m.msg_type as string) ?? 'text',
              direction: (m.direction as string) ?? 'inbound',
              status: (m.delivery_status as string) ?? 'sent',
            });
          }
        }
        for (const c of convs) {
          const preview = previewMap.get(c.id);
          if (preview) {
            if (!c.last_message_body) {
              c.last_message_body = preview.body.slice(0, 100);
              c.last_message_type = preview.type;
            }
            if (!c.last_message_direction) {
              c.last_message_direction = preview.direction;
              c.last_message_status = preview.status;
            }
          }
        }
      }
    }

    // Sort estable post-merge: la query principal y la de archived vienen
    // ya ordenadas individualmente, pero al concatenarlas se rompe el orden
    // global por last_message_at. Re-ordenamos para que la lista (normal +
    // archived overlay) tenga timestamps consistentes.
    convs.sort((a, b) => {
      const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return tb - ta;
    });

    // Skip re-render si la data nueva es funcionalmente igual a la cacheada
    // (mismos ids/timestamps/contadores/estados). Evita parpadeo en refresh.
    const prevCache = cacheRef.current.get(viewKey);
    cacheRef.current.set(viewKey, convs);
    preloadAvatars(convs);
    if (!prevCache || !areConvListsEqual(prevCache, convs)) {
      setConversationsRaw(convs);
    }
    setLoading(false);
  }, [tenantId, isAgent, userId, phoneLineId, inboxId]);

  const loadConversations = useCallback(async () => {
    await traceSpan('wa.conversations.load', { tenant_id: tenantId ?? '', status: statusFilter, agent_only: isAgent }, async () => {
      await fetchForFilter(statusFilter);
    });
  }, [fetchForFilter, statusFilter, tenantId, isAgent]);

  /**
   * Cargar la SIGUIENTE tanda de la vista actual (scroll infinito). Aditivo: si algo
   * falla o no se llama, la lista sigue siendo la 1ra página — nunca peor que hoy.
   * Cursor por keyset sobre last_message_at (la columna de orden); dedup por id para
   * que un empate de timestamp en el borde no duplique filas.
   */
  const loadMore = useCallback(async () => {
    if (!tenantId || loadingMore || !hasMore) return;
    // Cursor = el last_message_at más viejo YA cargado de la vista principal (no del
    // overlay de archivados, que se paginó con su propia lógica).
    const mainRows = conversations.filter((c) => matchesMainFilter(c.status, statusFilter) && c.last_message_at);
    const cursor = mainRows.length ? mainRows[mainRows.length - 1].last_message_at : null;
    if (!cursor) { setHasMore(false); return; }

    setLoadingMore(true);
    try {
      let q = supabase
        .from('wa_conversations')
        .select(WA_CONV_COLUMNS)
        .eq('tenant_id', tenantId)
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .lt('last_message_at', cursor)
        .limit(PAGE_SIZE);
      if (isAgent && userId) q = q.or(`assigned_agent_id.eq.${userId},assigned_agent_id.is.null`);
      if (inboxId) q = q.eq('inbox_id', inboxId);
      if (phoneLineId) q = q.eq('phone_line_id', phoneLineId);
      q = applyStatusFilter(q, statusFilter);

      const { data, error } = await q;
      if (error) { devLog.error('loadMore error:', error); return; }
      const page = (data ?? []) as unknown as Record<string, unknown>[];
      setHasMore(page.length >= PAGE_SIZE);
      if (page.length === 0) return;

      const mapped = page.map((c) => ({
        ...c,
        tags: (c.tags as string[]) ?? [],
        contact_photo_url: (c.contact_photo_url as string) ?? null,
        typing: false,
      })) as WaConversation[];

      // Append deduplicado por id (por si el borde del cursor solapó una fila).
      setConversations((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        const fresh = mapped.filter((c) => !seen.has(c.id));
        return fresh.length ? [...prev, ...fresh] : prev;
      });
      preloadAvatars(mapped);
    } finally {
      setLoadingMore(false);
    }
  }, [tenantId, loadingMore, hasMore, conversations, statusFilter, isAgent, userId, phoneLineId, inboxId, setConversations]);

  useEffect(() => { loadRef.current = loadConversations; void loadConversations(); }, [loadConversations]);

  // Auto-sync de fotos: invoca el MISMO endpoint que el botón
  // "Sincronizar fotos" (`wa-line-ops` con `action: sync_contact_photos`).
  // Patrón confirmado: ese endpoint funciona en producción. Antes
  // llamábamos `whatsapp-fetch-photos` que tiene lógica más compleja y
  // a veces no rellenaba todas. Replica dulti — el usuario no debería
  // apretar nada para ver fotos.
  //
  // Si hay filtro de canal, sync solo esa línea. Si no, sync todas las
  // líneas activas del tenant (porque sync_contact_photos requiere line_id).
  //
  // Throttle por (tenant, canal): no más de 1 sync cada 60s. La sync
  // server-side es idempotente — skipea conversations que ya tienen foto.
  useEffect(() => {
    if (!tenantId) return;
    const missing = conversations.filter((c) => !c.contact_photo_url);
    if (missing.length === 0) return;

    const triggerSync = (lineId: string) => {
      const key = `${tenantId}:${lineId}`;
      const last = lastAutoSyncAt.get(key) ?? 0;
      const now = Date.now();
      if (now - last < AUTO_SYNC_THROTTLE_MS) return;
      lastAutoSyncAt.set(key, now);
      void supabase.functions.invoke('wa-line-ops', {
        body: { action: 'sync_contact_photos', line_id: lineId },
      }).catch(() => { /* el realtime UPDATE del campo contact_photo_url
         empuja la foto a la UI cuando termina */ });
    };

    if (phoneLineId) {
      triggerSync(phoneLineId);
      return;
    }

    // Sin filtro de canal: sync todas las líneas activas que tienen al
    // menos 1 conv sin foto.
    const linesNeedingSync = new Set<string>();
    for (const c of missing) {
      if (c.phone_line_id) linesNeedingSync.add(c.phone_line_id);
    }
    for (const lineId of linesNeedingSync) {
      triggerSync(lineId);
    }
  }, [tenantId, phoneLineId, conversations]);

  return { conversations, setConversations, loading, loadError, loadConversations, loadRef, hasMore, loadingMore, loadMore };
}

// Throttle module-level: 1 sync por (tenant, canal) cada 60s para evitar
// ruido al cambiar de filtro varias veces seguidas. La sync server-side
// es idempotente y skipea conversations que ya tienen foto.
const lastAutoSyncAt = new Map<string, number>();
const AUTO_SYNC_THROTTLE_MS = 60_000;
